/**
 * Utilidades para manejo seguro de localStorage
 */

const STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  DEVICE_ID: 'device_id',
  PERMISSIONS: 'permissions',
  TOKEN_EXPIRY: 'token_expiry', // Timestamp de expiración del access token
} as const;

export const StorageUtil = {
  // Tokens
  setAccessToken(token: string): void {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
  },

  getAccessToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  },

  setRefreshToken(token: string): void {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
  },

  getRefreshToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  },

  // Expiración del token
  setTokenExpiry(expiresIn: number): void {
    // expiresIn está en segundos, convertir a timestamp
    const expiryTime = Date.now() + expiresIn * 1000;
    localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, expiryTime.toString());
  },

  getTokenExpiry(): number | null {
    const expiry = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);
    return expiry ? parseInt(expiry, 10) : null;
  },

  /**
   * Verifica si el token está por expirar
   * @param minutesBeforeExpiry - Minutos antes de la expiración para considerar el token como próximo a expirar
   * @returns true si el token expira en menos de los minutos especificados
   */
  isTokenExpiringSoon(minutesBeforeExpiry: number = 5): boolean {
    const expiry = this.getTokenExpiry();
    if (!expiry) return true;

    const now = Date.now();
    const timeUntilExpiry = expiry - now;
    const thresholdMs = minutesBeforeExpiry * 60 * 1000;

    return timeUntilExpiry < thresholdMs;
  },

  isTokenExpired(): boolean {
    const expiry = this.getTokenExpiry();
    if (!expiry) return true;

    return Date.now() >= expiry;
  },

  // Device ID
  setDeviceId(deviceId: string): void {
    localStorage.setItem(STORAGE_KEYS.DEVICE_ID, deviceId);
  },

  getDeviceId(): string | null {
    return localStorage.getItem(STORAGE_KEYS.DEVICE_ID);
  },

  // Permissions
  setPermissions(permissions: string): void {
    localStorage.setItem(STORAGE_KEYS.PERMISSIONS, permissions);
  },

  getPermissions(): string | null {
    return localStorage.getItem(STORAGE_KEYS.PERMISSIONS);
  },

  // Limpiar todo
  clearAll(): void {
    Object.values(STORAGE_KEYS).forEach((key) => {
      localStorage.removeItem(key);
    });
  },

  // Limpiar solo tokens (mantener deviceId)
  clearTokens(): void {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRY);
    localStorage.removeItem(STORAGE_KEYS.PERMISSIONS);
  },

  // Verificar si hay sesión activa
  hasValidSession(): boolean {
    return !!(this.getAccessToken() && this.getRefreshToken() && !this.isTokenExpired());
  },

  // Verificar si necesita refresh
  needsRefresh(): boolean {
    return this.isTokenExpiringSoon(5);
  },
};
